import { useMemo, useState } from "react";
import SalviaLayout from "@/components/SalviaLayout";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, Pencil, ShieldCheck, ShieldPlus } from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { usePageTitle } from "@/hooks/usePageTitle";

const LEVEL1_OPTIONS = [
  { value: "admin", label: "Admin" },
  { value: "channel", label: "Canal" },
  { value: "client", label: "Cliente" },
] as const;

const SERVICE_OPTIONS = [
  { value: "__platform__", label: "Sem serviço específico (Plataforma)" },
  { value: "nr1", label: "NR1" },
] as const;

type Level1Scope = (typeof LEVEL1_OPTIONS)[number]["value"];

type EditableProfile = {
  id?: number;
  key: string;
  name: string;
  description: string;
  level1Scope: Level1Scope;
  serviceKey: string;
  status: "draft" | "active" | "inactive";
  permissionKeys: string[];
};

const EMPTY_PROFILE: EditableProfile = {
  key: "",
  name: "",
  description: "",
  level1Scope: "channel",
  serviceKey: "nr1",
  status: "draft",
  permissionKeys: [],
};

export default function AccessProfiles() {
  usePageTitle("Perfis Funcionais");
  const [level1Scope, setLevel1Scope] = useState<Level1Scope>("channel");
  const [serviceKey, setServiceKey] = useState<string>("nr1");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingProfile, setEditingProfile] = useState<EditableProfile>(EMPTY_PROFILE);

  const permissionQuery = trpc.accessProfiles.listPermissions.useQuery();
  const profilesQuery = trpc.accessProfiles.listProfiles.useQuery({
    level1Scope,
    serviceKey: serviceKey === "__platform__" ? null : serviceKey,
  });

  const upsertProfile = trpc.accessProfiles.upsertProfile.useMutation({
    onSuccess: () => {
      toast.success("Perfil funcional salvo com sucesso.");
      setDialogOpen(false);
      setEditingProfile(EMPTY_PROFILE);
      profilesQuery.refetch();
    },
    onError: (error) => {
      toast.error(error.message || "Erro ao salvar perfil funcional.");
    },
  });

  const filteredPermissions = useMemo(() => {
    const permissions = permissionQuery.data?.permissions ?? [];
    return permissions.filter((permission: any) => {
      if (permission.level1Scope !== level1Scope) {
        return false;
      }

      if (serviceKey === "__platform__") {
        return permission.serviceKey === null;
      }

      return permission.serviceKey === serviceKey;
    });
  }, [level1Scope, permissionQuery.data?.permissions, serviceKey]);

  const modalPermissions = useMemo(() => {
    const permissions = permissionQuery.data?.permissions ?? [];
    return permissions.filter((permission: any) => {
      if (permission.level1Scope !== editingProfile.level1Scope) {
        return false;
      }

      if (editingProfile.serviceKey === "__platform__") {
        return permission.serviceKey === null;
      }

      return permission.serviceKey === editingProfile.serviceKey;
    });
  }, [editingProfile.level1Scope, editingProfile.serviceKey, permissionQuery.data?.permissions]);

  const openCreateDialog = () => {
    setEditingProfile({
      ...EMPTY_PROFILE,
      level1Scope,
      serviceKey,
    });
    setDialogOpen(true);
  };

  const openEditDialog = (profile: any) => {
    setEditingProfile({
      id: profile.id,
      key: profile.key,
      name: profile.name,
      description: profile.description ?? "",
      level1Scope: profile.level1Scope,
      serviceKey: profile.serviceKey ?? "__platform__",
      status: profile.status,
      permissionKeys: profile.permissionKeys ?? [],
    });
    setDialogOpen(true);
  };

  const togglePermission = (permissionKey: string, checked: boolean) => {
    setEditingProfile((current) => ({
      ...current,
      permissionKeys: checked
        ? Array.from(new Set([...current.permissionKeys, permissionKey]))
        : current.permissionKeys.filter((item) => item !== permissionKey),
    }));
  };

  const handleSave = () => {
    upsertProfile.mutate({
      id: editingProfile.id,
      key: editingProfile.key,
      name: editingProfile.name,
      description: editingProfile.description || null,
      level1Scope: editingProfile.level1Scope,
      serviceKey: editingProfile.serviceKey === "__platform__" ? null : editingProfile.serviceKey,
      status: editingProfile.status,
      permissionKeys: editingProfile.permissionKeys,
    });
  };

  return (
    <SalviaLayout title="Perfis Funcionais do Nível 2">
      <div className="space-y-6">
        <section className="rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
            <div className="space-y-2">
              <Badge variant="secondary">CRUD governado do Nível 2</Badge>
              <h1 className="text-3xl font-semibold tracking-tight text-slate-950">Perfis funcionais governados</h1>
              <p className="max-w-3xl text-sm leading-7 text-slate-600">
                Esta tela administra o catálogo funcional da segunda camada de acesso. Os perfis só podem ser compostos a partir
                das permissões atômicas oficiais e dentro do escopo compatível com o Nível 1 selecionado.
              </p>
            </div>
            <Button className="bg-[#002F59] text-white hover:bg-[#001a33]" onClick={openCreateDialog}>
              <ShieldPlus className="mr-2 h-4 w-4" />
              Novo perfil funcional
            </Button>
          </div>

          <div className="mt-6 grid gap-4 md:grid-cols-2 xl:grid-cols-4">
            <div className="space-y-2">
              <Label>Nível 1 alvo</Label>
              <Select value={level1Scope} onValueChange={(value) => setLevel1Scope(value as Level1Scope)}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o Nível 1" />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL1_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Serviço governado</Label>
              <Select value={serviceKey} onValueChange={setServiceKey}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o serviço" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Card className="rounded-2xl border-slate-200 p-4 shadow-none">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Permissões governadas</p>
              <p className="mt-2 text-3xl font-semibold text-slate-950">{filteredPermissions.length}</p>
            </Card>
            <Card className="rounded-2xl border-slate-200 p-4 shadow-none">
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">Perfis listados</p>
              <p className="mt-2 text-3xl font-semibold text-slate-950">{profilesQuery.data?.profiles?.length ?? 0}</p>
            </Card>
          </div>
        </section>

        <section className="grid gap-6 xl:grid-cols-[1.25fr_0.75fr]">
          <Card className="rounded-[2rem] border-slate-200 p-6 shadow-sm">
            <div className="flex items-center justify-between gap-4">
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Perfis disponíveis</h2>
                <p className="mt-1 text-sm text-slate-600">
                  Perfis base e perfis customizados compatíveis com o escopo filtrado.
                </p>
              </div>
              {profilesQuery.isLoading && <Loader2 className="h-5 w-5 animate-spin text-slate-500" />}
            </div>

            <div className="mt-6 space-y-4">
              {(profilesQuery.data?.profiles ?? []).map((profile: any) => (
                <div key={profile.key} className="rounded-2xl border border-slate-200 p-5">
                  <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                    <div className="space-y-2">
                      <div className="flex flex-wrap items-center gap-2">
                        <h3 className="text-lg font-semibold text-slate-950">{profile.name}</h3>
                        <Badge variant="secondary">{profile.level1Scope}</Badge>
                        <Badge variant={profile.status === "active" ? "secondary" : "outline"}>{profile.status}</Badge>
                        {profile.isBaseProfile && <Badge className="bg-[#C0FF00] text-black hover:bg-[#C0FF00]">Perfil-base</Badge>}
                      </div>
                      <p className="text-sm text-slate-600">{profile.description || "Sem descrição registrada."}</p>
                      <div className="flex flex-wrap gap-2">
                        {(profile.permissionKeys ?? []).map((permissionKey: string) => (
                          <Badge key={permissionKey} variant="outline">{permissionKey}</Badge>
                        ))}
                      </div>
                    </div>
                    <Button variant="outline" onClick={() => openEditDialog(profile)}>
                      <Pencil className="mr-2 h-4 w-4" />
                      Editar
                    </Button>
                  </div>
                </div>
              ))}

              {!profilesQuery.isLoading && (profilesQuery.data?.profiles?.length ?? 0) === 0 && (
                <Alert>
                  <AlertDescription>
                    Nenhum perfil funcional foi encontrado para o filtro atual. Você pode criar um perfil governado a partir do catálogo disponível.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          </Card>

          <Card className="rounded-[2rem] border-slate-200 p-6 shadow-sm">
            <div className="flex items-center gap-3">
              <ShieldCheck className="h-5 w-5 text-[#0b3b6f]" />
              <div>
                <h2 className="text-xl font-semibold text-slate-950">Permissões oficiais do filtro</h2>
                <p className="mt-1 text-sm text-slate-600">Somente permissões oficiais podem compor perfis do Nível 2.</p>
              </div>
            </div>
            <div className="mt-6 space-y-3">
              {filteredPermissions.map((permission: any) => (
                <div key={permission.key} className="rounded-2xl border border-slate-200 p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <p className="font-medium text-slate-950">{permission.name}</p>
                    <Badge variant="outline">{permission.key}</Badge>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{permission.description}</p>
                </div>
              ))}
            </div>
          </Card>
        </section>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>{editingProfile.id ? "Editar perfil funcional" : "Novo perfil funcional"}</DialogTitle>
            <DialogDescription>
              Perfis do Nível 2 só podem ser compostos com permissões oficiais compatíveis com o Nível 1 e o serviço selecionados.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Chave técnica</Label>
              <Input value={editingProfile.key} onChange={(event) => setEditingProfile((current) => ({ ...current, key: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Nome do perfil</Label>
              <Input value={editingProfile.name} onChange={(event) => setEditingProfile((current) => ({ ...current, name: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Nível 1</Label>
              <Select value={editingProfile.level1Scope} onValueChange={(value) => setEditingProfile((current) => ({ ...current, level1Scope: value as Level1Scope, permissionKeys: [] }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o nível" />
                </SelectTrigger>
                <SelectContent>
                  {LEVEL1_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Serviço</Label>
              <Select value={editingProfile.serviceKey} onValueChange={(value) => setEditingProfile((current) => ({ ...current, serviceKey: value, permissionKeys: [] }))}>
                <SelectTrigger>
                  <SelectValue placeholder="Selecione o serviço" />
                </SelectTrigger>
                <SelectContent>
                  {SERVICE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Descrição</Label>
            <Textarea value={editingProfile.description} onChange={(event) => setEditingProfile((current) => ({ ...current, description: event.target.value }))} />
          </div>

          <div className="space-y-2">
            <Label>Status</Label>
            <Select value={editingProfile.status} onValueChange={(value) => setEditingProfile((current) => ({ ...current, status: value as EditableProfile["status"] }))}>
              <SelectTrigger>
                <SelectValue placeholder="Selecione o status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="draft">Rascunho</SelectItem>
                <SelectItem value="active">Ativo</SelectItem>
                <SelectItem value="inactive">Inativo</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-3">
            <Label>Permissões atômicas governadas</Label>
            <div className="grid gap-3 max-h-[20rem] overflow-y-auto rounded-2xl border border-slate-200 p-4">
              {permissionQuery.isLoading ? (
                <div className="flex items-center gap-2 text-sm text-slate-500">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Carregando catálogo governado...
                </div>
              ) : modalPermissions.length === 0 ? (
                <Alert>
                  <AlertDescription>
                    Não há permissões governadas disponíveis para o filtro selecionado.
                  </AlertDescription>
                </Alert>
              ) : (
                modalPermissions.map((permission: any) => {
                  const checked = editingProfile.permissionKeys.includes(permission.key);
                  return (
                    <label key={permission.key} className="flex items-start justify-between gap-4 rounded-2xl border border-slate-200 p-4 hover:bg-slate-50">
                      <div className="space-y-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium text-slate-950">{permission.name}</span>
                          <Badge variant="outline">{permission.key}</Badge>
                        </div>
                        <p className="text-sm leading-6 text-slate-600">{permission.description}</p>
                      </div>
                      <input type="checkbox" checked={checked} onChange={(event) => togglePermission(permission.key, event.target.checked)} className="mt-1 h-4 w-4 rounded border-slate-300" />
                    </label>
                  );
                })
              )}
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
            <Button className="bg-[#002F59] text-white hover:bg-[#001a33]" onClick={handleSave} disabled={upsertProfile.isPending}>
              {upsertProfile.isPending ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Salvando...</> : "Salvar perfil"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </SalviaLayout>
  );
}
